import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCode } from 'antd';
import { FaPix } from 'react-icons/fa6';
import { MdCheckCircle, MdContentCopy, MdDone } from 'react-icons/md';
import { toast } from 'react-toastify';
import styles from './style.module.scss';
import { useCart } from '../../contexts/CartProvider';
import { useSaldo } from '../../contexts/SaldoContext';
import { useGirosGanhos } from '../../contexts/GirosGanhosContext';
import { useCheckoutTotals, formatCurrency } from '../../hooks/useCheckoutTotals';
import { api } from '../../services/apiClient';
import CheckoutBase from '../CheckoutBase';
import logoSvg from '../../assets/tribodasorte 2.svg';

/** Intervalo entre verificações: cai rápido no começo e vai espaçando. */
const intervaloVerificacao = (decorridoMs) => {
  if (decorridoMs < 60_000) return 3;
  if (decorridoMs < 300_000) return 6;
  return 12;
};

const Pix = ({ items, couponCode, setCouponCode, validarCupon }) => {
  const [urlPix, setUrlPix] = useState('');
  const [pixId, setPixId] = useState();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pago, setPago] = useState(false);
  const [segundosParaVerificar, setSegundosParaVerificar] = useState(null);
  const [isSaldoRoute] = useState(() => window.location.href.includes('/saldo'));

  const copyTimer = useRef(null);

  const { clearCart, coupon, setTotal } = useCart();
  const { fetchSaldo } = useSaldo();
  const { mostrarGirosGanhos } = useGirosGanhos();
  const navigate = useNavigate();

  const resumo = useCheckoutTotals(items, coupon);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const gerarPix = async () => {
    setLoading(true);
    try {
      const rota = isSaldoRoute ? '/carrega_saldo_pix' : '/comprar_qr_code';
      const payload = isSaldoRoute
        ? { total: resumo.total }
        : {
            cupom_desconto_id: coupon?.id,
            boloes: items,
            quantity: resumo.quantidade,
            total: resumo.total,
          };

      if (!isSaldoRoute) setTotal(resumo.total);

      const { data } = await api.post(rota, payload);

      if (data.id && data.init_point) {
        setUrlPix(data.init_point);
        setPixId(data.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error('Não foi possível gerar o código Pix. Tente novamente.');
      }

      fetchSaldo();
    } catch (e) {
      console.log('e', e.message);
      toast.error('Não foi possível gerar o código Pix. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const copiarCodigo = async () => {
    try {
      await navigator.clipboard.writeText(urlPix);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 4000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.warn('Não foi possível copiar. Selecione o código manualmente.');
    }
  };

  const verificarPagamento = useCallback(async () => {
    if (!pixId) return false;

    try {
      const { data } = await api.post('/verificar_pix', { id_pix: pixId });
      if (data.ok === true) {
        setPago(true);
        clearCart();
        fetchSaldo();
        mostrarGirosGanhos(data.giros_creditados);
        toast.success('Pagamento confirmado!', { position: 'top-right', autoClose: 5000, theme: 'light' });

        // Deixa a confirmação visível por um instante antes de trocar de tela.
        setTimeout(() => {
          window.scrollTo(0, 0);
          navigate(`/compra_concluida?idPagamento=${pixId}`);
        }, 1600);

        return true;
      }
    } catch (error) {
      console.log('Error checking payment status:', error.message);
    }

    return false;
  }, [pixId, clearCart, fetchSaldo, mostrarGirosGanhos, navigate]);

  /**
   * Verificação do pagamento. Antes era um setInterval fixo de 10s enquanto a tela
   * anunciava "nova verificação em 30s" — o contador não tinha relação com o polling.
   * Agora o contador exibido É o polling: 3s no primeiro minuto, 6s até os 5 min e
   * 12s depois disso. Congela com a aba em segundo plano e verifica na hora que ela volta.
   */
  useEffect(() => {
    if (!pixId || pago) return undefined;

    let cancelado = false;
    let verificando = false;
    let restante = 3;
    const inicio = Date.now();

    setSegundosParaVerificar(restante);

    const verificarAgora = async () => {
      if (cancelado || verificando) return;
      verificando = true;
      try {
        const confirmado = await verificarPagamento();
        if (confirmado || cancelado) return;
      } finally {
        verificando = false;
      }
      restante = intervaloVerificacao(Date.now() - inicio);
      setSegundosParaVerificar(restante);
    };

    const tick = setInterval(() => {
      if (cancelado || document.visibilityState === 'hidden') return;
      restante -= 1;
      setSegundosParaVerificar(Math.max(restante, 0));
      if (restante <= 0) verificarAgora();
    }, 1000);

    const aoVoltarParaAba = () => {
      if (document.visibilityState === 'visible') verificarAgora();
    };
    document.addEventListener('visibilitychange', aoVoltarParaAba);

    return () => {
      cancelado = true;
      clearInterval(tick);
      document.removeEventListener('visibilitychange', aoVoltarParaAba);
    };
  }, [pixId, pago, verificarPagamento]);

  if (pago) {
    return (
      <div className={styles.successState}>
        <MdCheckCircle className={styles.successIcon} />
        <h2>Pagamento confirmado!</h2>
        <p>Recebemos {formatCurrency(resumo.total)}. Estamos abrindo o seu comprovante...</p>
      </div>
    );
  }

  // Código gerado: a tela vira só o pagamento, sem resumo nem cupom competindo por atenção.
  if (urlPix) {
    return (
      <div className={styles.pixPanel}>
        <header className={styles.pixHeader}>
          <FaPix />
          <div>
            <h2>Pix gerado</h2>
            <p>Pague {formatCurrency(resumo.total)} para confirmar o pedido</p>
          </div>
        </header>

        <button type='button' className={`${styles.copyButton} ${copied ? styles.copied : ''}`} onClick={copiarCodigo}>
          {copied ? <MdDone /> : <MdContentCopy />}
          {copied ? 'Código copiado!' : 'Copiar código Pix'}
        </button>

        <div className={styles.divider}>
          <span>ou aponte a câmera</span>
        </div>

        <div className={styles.qrBox}>
          <QRCode errorLevel='H' value={urlPix} icon={logoSvg} size={200} bordered={false} />
        </div>

        <div className={styles.statusBox}>
          <span className={styles.pulse} aria-hidden='true' />
          <div>
            <strong>Aguardando o pagamento</strong>
            <p>
              A tela muda sozinha assim que cair
              {segundosParaVerificar !== null && ` · nova checagem em ${segundosParaVerificar}s`}
            </p>
          </div>
        </div>

        <ol className={styles.steps}>
          <li><span>1</span> Abra o app do seu banco</li>
          <li><span>2</span> Escolha Pix › Pix Copia e Cola ou QR Code</li>
          <li><span>3</span> Confirme — a liberação é imediata</li>
        </ol>

        <details className={styles.rawCode}>
          <summary>Ver o código completo</summary>
          <textarea readOnly value={urlPix} rows={3} onFocus={(e) => e.target.select()} />
        </details>
      </div>
    );
  }

  return (
    <CheckoutBase
      resumo={resumo}
      couponCode={couponCode}
      setCouponCode={setCouponCode}
      validarCupon={validarCupon}
      mostrarCupom={!isSaldoRoute}
      metodoPix
      loading={loading}
      onConfirm={gerarPix}
      confirmLabel={`Pagar ${formatCurrency(resumo.total)} com Pix`}
    >
      <div className={styles.cardBox}>
        <div className={styles.sectionTitle}>
          <FaPix />
          <h2>Pagamento via Pix</h2>
        </div>
        <p className={styles.sectionDescription}>
          Geramos o QR Code na hora. A confirmação costuma sair em poucos segundos e o
          pedido é liberado automaticamente.
        </p>

        <ul className={styles.benefits}>
          <li>Sem precisar cadastrar cartão</li>
          <li>Funciona em qualquer banco</li>
          <li>Confirmação automática, sem enviar comprovante</li>
        </ul>
      </div>
    </CheckoutBase>
  );
};

export default Pix;
